import {Component, ElementRef, inject, Input, OnChanges, OnDestroy, signal, SimpleChanges, ViewChild} from '@angular/core';
import {ChatLibraryData, ChatLibraryService, CREATIVES_PAGE_SIZE} from '../../services/chat-library.service';
import {LibraryHeaderComponent} from '../library-header/library-header.component';
import {LibraryGroupListComponent} from '../library-group-list/library-group-list.component';
import {AssetVersionPipe} from '../../../../../pipes/asset-version.pipe';
import {ChatMessage} from '../../../copilot.model';
import {PosthogService} from '../../../../../services/posthog.service';
import {PosthogEventName} from '../../../../../services/types';

const SENTINEL_ROOT_MARGIN = '200px';

@Component({
  selector: 'app-chat-library-drawer',
  standalone: true,
  imports: [LibraryHeaderComponent, LibraryGroupListComponent, AssetVersionPipe],
  templateUrl: './chat-library-drawer.component.html',
  styleUrl: './chat-library-drawer.component.scss',
})
export class ChatLibraryDrawerComponent implements OnChanges, OnDestroy {
  private readonly chatLibraryService = inject(ChatLibraryService);
  private readonly posthogService = inject(PosthogService);
  private intersectionObserver: IntersectionObserver | undefined;
  private readonly seenMessageIds = new Set<string>();
  private remainingCreatives: ChatLibraryData[] = [];
  private isLoadingBatch = false;
  // Bumped on chat switch so a batch that was in flight when the library reset is discarded
  private loadEpoch = 0;
  private sentinelEl: ElementRef<HTMLElement> | undefined;

  @ViewChild('sentinel') set sentinel(el: ElementRef<HTMLElement> | undefined) {
    this.sentinelEl = el;
    this.observeSentinel();
  }

  @Input() chatId: string | null = null;
  @Input() messages: ChatMessage[] = [];

  readonly state = this.chatLibraryService.state;
  readonly hasMore = signal<boolean>(false);
  // The last batch failed; it stays queued and is sent again only when the user asks.
  readonly loadFailed = signal<boolean>(false);
  // Every batch was sent and nothing is left to load.
  readonly exhausted = signal<boolean>(false);
  readonly skeletonPlaceholders = Array(6);
  readonly loadMorePlaceholders = Array(2);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chatId']) {
      this.resetForNewChat();
    }
    if (!changes['messages']) {
      return;
    }
    const newMessages = this.messages.filter(msg => this.hasLibraryData(msg) && !this.seenMessageIds.has(msg.id));
    if (newMessages.length === 0) {
      return;
    }
    for (const message of newMessages) {
      this.seenMessageIds.add(message.id);
      this.enqueueMessageChunks(message);
    }
    this.exhausted.set(false);
    void this.loadNextBatch();
    this.hasMore.set(this.remainingCreatives.length > 0);
  }

  private resetForNewChat(): void {
    this.loadEpoch++;
    this.isLoadingBatch = false;
    this.seenMessageIds.clear();
    this.remainingCreatives = [];
    this.hasMore.set(false);
    this.loadFailed.set(false);
    this.exhausted.set(false);
    this.chatLibraryService.resetLibrary();
  }

  private hasLibraryData(message: ChatMessage): boolean {
    return !!message.chatLibrary?.chatLibrary.length || !!message.creativesURLs?.length;
  }

  // chatLibrary itemIds drive chunking; creativeUrls are only used as a fallback
  // for messages that have no chatLibrary metadata
  private enqueueMessageChunks(message: ChatMessage): void {
    const libraryItems = message.chatLibrary?.chatLibrary ?? [];
    if (libraryItems.length > 0) {
      for (let i = 0; i < libraryItems.length; i += CREATIVES_PAGE_SIZE) {
        this.remainingCreatives.push({
          id: message.id,
          chatLibrary: {...message.chatLibrary!, chatLibrary: libraryItems.slice(i, i + CREATIVES_PAGE_SIZE)},
        });
      }
      return;
    }
    const urls = message.creativesURLs ?? [];
    for (let i = 0; i < urls.length; i += CREATIVES_PAGE_SIZE) {
      this.remainingCreatives.push({
        id: message.id,
        chatLibrary: undefined,
        creativeUrls: urls.slice(i, i + CREATIVES_PAGE_SIZE),
      });
    }
  }

  private itemCount(data: ChatLibraryData): number {
    return data.chatLibrary?.chatLibrary.length ?? data.creativeUrls?.length ?? 0;
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  togglePanel(): void {
    if (this.state()?.isOpen) {
      this.chatLibraryService.closePanel();
    } else {
      this.chatLibraryService.openPanel('toggle_button');
    }
  }

  onClosePanel(): void {
    this.posthogService.capture(PosthogEventName.CHAT_LIBRARY_PANEL_CLOSED);
    this.chatLibraryService.closePanel();
  }

  retryFailedBatch(): void {
    this.loadFailed.set(false);
    this.chatLibraryService.clearLoadError();
    void this.loadNextBatch();
  }

  private observeSentinel(): void {
    this.intersectionObserver?.disconnect();
    if (!this.sentinelEl || this.loadFailed()) {
      return;
    }
    this.intersectionObserver ??= new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          void this.loadNextBatch();
        }
      },
      {rootMargin: SENTINEL_ROOT_MARGIN}
    );
    this.intersectionObserver.observe(this.sentinelEl.nativeElement);
  }

  private async loadNextBatch(): Promise<void> {
    if (this.isLoadingBatch || this.remainingCreatives.length === 0) {
      return;
    }
    this.isLoadingBatch = true;
    const epoch = this.loadEpoch;
    const batch = this.takeNextBatch();
    let loadNext = false;
    try {
      const append = this.state().groups.length > 0;
      const {found, failed} = await this.chatLibraryService.loadCreativeLibrary(batch, append);
      if (epoch !== this.loadEpoch) {
        return;
      }
      if (failed) {
        // Keep the batch so a retry sends it again; a failed request is not an empty answer.
        this.remainingCreatives.unshift(...batch);
        this.loadFailed.set(true);
      } else if (found === 0) {
        // An empty chunk drops the rest of its answer, so one answer with unresolvable
        // creatives does not block the others.
        const emptyMessageIds = new Set(batch.map(data => data.id));
        this.remainingCreatives = this.remainingCreatives.filter(data => !emptyMessageIds.has(data.id));
        loadNext = this.remainingCreatives.length > 0;
      }
    } finally {
      if (epoch === this.loadEpoch) {
        this.isLoadingBatch = false;
        this.hasMore.set(this.remainingCreatives.length > 0);
        this.exhausted.set(this.remainingCreatives.length === 0);
        this.observeSentinel();
      }
    }
    if (loadNext) {
      await this.loadNextBatch();
    }
  }

  private takeNextBatch(): ChatLibraryData[] {
    const batch: ChatLibraryData[] = [];
    let creativeCount = 0;
    while (this.remainingCreatives.length > 0) {
      const nextCount = this.itemCount(this.remainingCreatives[0]);
      if (batch.length > 0 && creativeCount + nextCount > CREATIVES_PAGE_SIZE) {
        break;
      }
      batch.push(this.remainingCreatives.shift()!);
      creativeCount += nextCount;
    }
    return batch;
  }
}
